import { getAppointmentStatusMeta } from './appointmentsQuery';

const escapeHtml = (value) => {
    if (value == null || value === '') {
        return '';
    }

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const getPatientName = (patient = {}) => {
    const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();

    return name || '—';
};

const getDoctorName = (doctor = {}) => {
    const name = [doctor.firstName, doctor.lastName].filter(Boolean).join(' ').trim();

    return name ? `Dr. ${name}` : '—';
};

const getContactLines = (patient = {}) => {
    const lines = [];

    if (patient.email) {
        lines.push(escapeHtml(patient.email));
    }

    if (patient.cellNumber) {
        lines.push(escapeHtml(patient.cellNumber));
    }

    if (patient.phoneNumber) {
        lines.push(escapeHtml(patient.phoneNumber));
    }

    return lines.length ? lines.join('<br />') : '—';
};

const buildAppointmentRows = (appointments) =>
    appointments
        .map((appt) => {
            const patient = appt.patient || {};
            const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
            const preferredName = patient.preferredName
                ? `<div class="muted">${escapeHtml(patient.preferredName)}</div>`
                : '';

            return `
                <tr>
                    <td>
                        <strong>${escapeHtml(getPatientName(patient))}</strong>
                        ${preferredName}
                    </td>
                    <td>${escapeHtml(appt.startTime || '—')} – ${escapeHtml(appt.endTime || '—')}</td>
                    <td>${escapeHtml(getDoctorName(appt.doctor || {}))}</td>
                    <td>${escapeHtml(appt.reason || '—')}</td>
                    <td>${getContactLines(patient)}</td>
                    <td>${escapeHtml(statusMeta.label)}</td>
                </tr>
            `;
        })
        .join('');

const buildPrintDocument = ({ appointments, title, subtitle }) => {
    const rows = buildAppointmentRows(appointments);
    const subtitleHtml = subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
        @page {
            size: letter landscape;
            margin: 0.45in;
        }

        * {
            box-sizing: border-box;
        }

        body {
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #181c32;
            background: #fff;
        }

        h1 {
            margin: 0 0 0.25rem;
            font-size: 1.1rem;
            font-weight: 400;
        }

        .subtitle {
            margin: 0 0 0.85rem;
            font-size: 0.78rem;
            color: #78829d;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.78rem;
        }

        thead th {
            text-align: left;
            padding: 0.45rem 0.5rem;
            border-bottom: 2px solid #d8dbe5;
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #78829d;
            white-space: nowrap;
        }

        tbody td {
            padding: 0.5rem;
            border-bottom: 1px solid #eceef3;
            vertical-align: top;
        }

        tbody tr:nth-child(even) {
            background: #fafbfc;
        }

        .muted {
            margin-top: 0.15rem;
            font-size: 0.72rem;
            color: #78829d;
        }

        .empty {
            padding: 2rem 0;
            text-align: center;
            color: #78829d;
            font-size: 0.78rem;
        }
    </style>
</head>
<body>
    <h1>${escapeHtml(title)}</h1>
    ${subtitleHtml}
    ${
        appointments.length
            ? `<table>
                <thead>
                    <tr>
                        <th>Patient</th>
                        <th>Time</th>
                        <th>Doctor</th>
                        <th>Reason</th>
                        <th>Contact</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>`
            : '<p class="empty">No appointments to print.</p>'
    }
</body>
</html>`;
};

const removePrintFrame = (frame) => {
    if (frame?.parentNode) {
        frame.parentNode.removeChild(frame);
    }
};

export const printAppointmentsList = ({ appointments, title, subtitle }) => {
    const html = buildPrintDocument({ appointments, title, subtitle });

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Appointments print preview');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';

    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDocument = frameWindow?.document;

    if (!frameWindow || !frameDocument) {
        removePrintFrame(iframe);
        return false;
    }

    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    const cleanup = () => {
        removePrintFrame(iframe);
    };

    const triggerPrint = () => {
        try {
            frameWindow.focus();
            frameWindow.print();
        } catch (error) {
            console.error(error);
            cleanup();
        }
    };

    frameWindow.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(cleanup, 60000);

    if (frameDocument.readyState === 'complete') {
        window.setTimeout(triggerPrint, 150);
    } else {
        iframe.addEventListener('load', () => window.setTimeout(triggerPrint, 150), { once: true });
    }

    return true;
};
